// import { LocationMapDialog } from '@/components/controls';
import { LocationMapDialog } from '@/components/controls';
import { getGroupType } from '@/utils';
import { Done as DoneIcon } from '@mui/icons-material';
import { Masonry } from '@mui/lab';
import {
  alpha,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useMemo, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { MissingPerson } from './MissingPerson';
import { SharedMissingPerson } from './SharedMissingPerson';
import { SharedMissingPersonEditor } from './SharedMissingPersonEditor';
import { useSharedMissingPersons } from './useSharedMissingPersons';
import { useNicheMissingPersons } from './useNicheMissingPersons';
import { useNicheMissingPerson } from './useNicheMissingPerson';

export function MissingPersons({ groupCodes, onComplete }) {
  const { data: missingPersons } = useNicheMissingPersons(
    getGroupType('SECTOR'),
    groupCodes,
  );
  const { data: highRiskMissingPersons } = useNicheMissingPersons(
    getGroupType('BASIC_COMMAND_UNIT'),
    groupCodes,
    { highRisk: true },
  );
  const { data: sharedMissingPersons } = useSharedMissingPersons(groupCodes);
  const sectorMissingPersons = useMemo(
    () =>
      (missingPersons ?? []).filter(
        ({ id }) =>
          !(sharedMissingPersons ?? []).some(({ personId }) => personId === id),
      ),
    [missingPersons, sharedMissingPersons],
  );
  const { width, ref } = useResizeDetector();
  const highRiskColumns = useMemo(() => Math.floor(width / 328) || 1, [width]);
  const lowRiskColumns = useMemo(() => Math.floor(width / 248) || 1, [width]);
  const [shareIds, setShareIds] = useState(null);
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const { data: selectedMissingPerson } =
    useNicheMissingPerson(selectedPersonId);

  const handleShowMapClick = (id) => () => {
    setSelectedPersonId(id);
  };

  function handleMapClose() {
    setSelectedPersonId(null);
  }

  function handleCompleteClick() {
    onComplete();
  }

  const handleShareClick = (code, personId) => () => {
    setShareIds({ code, personId });
  };

  function handleEditorClose() {
    setShareIds(null);
  }

  return (
    <Box sx={{ px: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={(theme) => ({
          alignItems: 'center',
          position: 'sticky',
          top: 48,
          backgroundColor: alpha(theme.palette.background.paper, 0.8),
          zIndex: 700,
          pl: 1,
          py: 1,
        })}
      >
        <Typography variant="h6">Missing Persons</Typography>
        <Box>
          <Tooltip title="Done" placement="right">
            <IconButton onClick={handleCompleteClick}>
              <DoneIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
      </Stack>
      <Stack spacing={1} sx={{ pb: 1 }} ref={ref}>
        {highRiskMissingPersons?.length > 0 && (
          <Fragment>
            <Typography variant="subtitle1" sx={{ pl: 1 }}>
              High Risk in my BCU
            </Typography>
            <Masonry spacing={1} columns={highRiskColumns}>
              {highRiskMissingPersons.map((missingPerson) => (
                <MissingPerson
                  key={missingPerson.id}
                  missingPerson={missingPerson}
                  onShowMapClick={handleShowMapClick(missingPerson.id)}
                  onShareClick={handleShareClick}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
        {sharedMissingPersons?.length > 0 && (
          <Fragment>
            <Typography variant="subtitle1" sx={{ pl: 1 }}>
              Shared
            </Typography>
            <Masonry spacing={1} columns={lowRiskColumns}>
              {sharedMissingPersons.map(({ personId, reason }) => (
                <SharedMissingPerson
                  key={personId}
                  personId={personId}
                  reason={reason}
                  onShowMapClick={handleShowMapClick(personId)}
                  onShareClick={handleShareClick}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
        {sectorMissingPersons?.length > 0 && (
          <Fragment>
            <Typography variant="subtitle1" sx={{ pl: 1 }}>
              Medium & Low Risk in my Sector
            </Typography>
            <Masonry spacing={1} columns={lowRiskColumns}>
              {sectorMissingPersons.map((missingPerson) => (
                <MissingPerson
                  key={missingPerson.id}
                  missingPerson={missingPerson}
                  onShowMapClick={handleShowMapClick(missingPerson.id)}
                  onShareClick={handleShareClick}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
      </Stack>
      <SharedMissingPersonEditor
        ids={shareIds}
        isOpen={Boolean(shareIds)}
        onClose={handleEditorClose}
      />
      <LocationMapDialog
        point={selectedMissingPerson?.addresses?.[0].point}
        title={`${selectedMissingPerson?.forenames} ${selectedMissingPerson?.surname} missing from`}
        description={selectedMissingPerson?.addresses?.[0]?.label}
        isOpen={Boolean(selectedPersonId)}
        onClose={handleMapClose}
      />
    </Box>
  );
}
